import { SyntheticEvent, useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Skeleton } from '@mui/material';
import {
  ExpressCheckoutElement,
  PaymentRequestButtonElement,
  useStripe,
} from '@stripe/react-stripe-js';
import {
  PaymentRequest,
  PaymentRequestPaymentMethodEvent,
  StripeExpressCheckoutElementClickEvent,
  StripeExpressCheckoutElementConfirmEvent,
  StripeExpressCheckoutElementReadyEvent,
} from '@stripe/stripe-js';

import { PreAuthFailedDialog } from './PreAuthFailedDialog';

import {
  FEATURE_FLAGS,
  PAYMENT_GATEWAY,
  PAYMENT_METHOD,
  TRACKING_BUTTON,
} from '~common';
import { LoadingButton } from '~components/atoms/custom';
import { LocalizationTypography } from '~components/organisms/localization';
import { useUserAgent } from '~hooks';
import { useRentCharger, useWalletCheckout } from '~hooks/payment';
import {
  isSelectedCampusCard,
  useEventTrackingContext,
  usePaymentStepContext,
  useStationPricingContext,
} from '~providers';
import { checkEnabledFeatureFlag } from '~utils';

type Props = {
  disabled?: boolean;
};

type WalletAvailability = 'checking' | 'available' | 'unavailable';

/**
 * New wallet flow (ENG-7890): `ExpressCheckoutElement` + `stripe.confirmSetup`.
 *
 * On the ECE `confirm` event it collects the wallet payment method through a
 * server SetupIntent (see `useWalletCheckout`) and then starts the rental. The
 * host `<Elements>` must be mounted in `mode: 'setup'` (see `RentChargerStep`).
 */
const ExpressRentChargerButton = ({ disabled = false }: Props) => {
  const { selectedPaymentMethod, selectedPricing } = usePaymentStepContext();
  const { availableWalletPaymentMethod } = useUserAgent();
  const {
    handleRentCharger,
    isRentingCharger,
    handleSetIsRentingCharger,
    isPaymentErrorDialogOpen,
    handleClosePaymentErrorDialog,
  } = useRentCharger();
  const { recordBtnClicked } = useEventTrackingContext();
  const { confirmWalletPaymentMethod, walletError } = useWalletCheckout();

  const [walletAvailability, setWalletAvailability] =
    useState<WalletAvailability>('checking');

  useEffect(() => {
    if (walletError) {
      toast.error(walletError);
    }
  }, [walletError]);

  const handleRentChargerForSelectedMethod = useCallback(
    (event: SyntheticEvent<HTMLButtonElement>) => {
      if (!selectedPaymentMethod) {
        return;
      }

      if (selectedPaymentMethod === 'WALLET') {
        toast.error('Wallet not supported for this browser');
        return;
      }

      // Campus card: charge via the campus-card gateway. The id is the linked
      // campus card payment id; no Stripe `method` applies.
      if (isSelectedCampusCard(selectedPaymentMethod)) {
        handleRentCharger(
          {
            id: selectedPaymentMethod.card.id,
            gateway: PAYMENT_GATEWAY.CAMPUS_CARD,
          },
          event,
          undefined,
          selectedPricing?.id,
        );
        recordBtnClicked(TRACKING_BUTTON.EJECT_BATTERY, {
          paymentMethod: PAYMENT_GATEWAY.CAMPUS_CARD,
        });
        return;
      }

      handleRentCharger(
        {
          id: selectedPaymentMethod.paymentMethodId,
          method: PAYMENT_METHOD.CREDIT_CARD,
        },
        event,
        undefined,
        selectedPricing?.id,
      );
      recordBtnClicked(TRACKING_BUTTON.EJECT_BATTERY, {
        paymentMethod: PAYMENT_METHOD.CREDIT_CARD,
      });
    },
    [
      handleRentCharger,
      recordBtnClicked,
      selectedPaymentMethod,
      selectedPricing,
    ],
  );

  const handleReady = useCallback(
    (event: StripeExpressCheckoutElementReadyEvent) => {
      const methods = event.availablePaymentMethods;
      const isSelectedWalletAvailable =
        availableWalletPaymentMethod === PAYMENT_METHOD.APPLE_PAY
          ? methods?.applePay
          : methods?.googlePay;

      setWalletAvailability(
        isSelectedWalletAvailable ? 'available' : 'unavailable',
      );
    },
    [availableWalletPaymentMethod],
  );

  const handleWalletClick = useCallback(
    (event: StripeExpressCheckoutElementClickEvent) => {
      handleSetIsRentingCharger(true);
      // Must be called within 1s of the click for the wallet sheet to open.
      event.resolve();
    },
    [handleSetIsRentingCharger],
  );

  const handleWalletConfirm = useCallback(
    async (event: StripeExpressCheckoutElementConfirmEvent) => {
      const result = await confirmWalletPaymentMethod(event);
      if (!result) {
        handleSetIsRentingCharger(false);
        return;
      }

      await handleRentCharger(
        { id: result.paymentMethodId, method: result.method },
        undefined,
        undefined,
        selectedPricing?.id,
      );

      recordBtnClicked(TRACKING_BUTTON.EJECT_BATTERY, {
        paymentMethod: result.method,
      });
    },
    [
      confirmWalletPaymentMethod,
      handleRentCharger,
      handleSetIsRentingCharger,
      recordBtnClicked,
      selectedPricing,
    ],
  );

  const handleWalletCancel = useCallback(() => {
    handleSetIsRentingCharger(false);
  }, [handleSetIsRentingCharger]);

  const selectedMethodButton = (
    <LoadingButton
      variant="contained"
      color="primary"
      onClick={handleRentChargerForSelectedMethod}
      isLoading={isRentingCharger}
      disabled={isRentingCharger || disabled}
    >
      <LocalizationTypography
        variant="Poppins/Subheading-md"
        textId="app.rentalPage.newStepThree.text.rentCharger"
        defaultText="Rent Charger"
      />
    </LoadingButton>
  );

  const isWallet = selectedPaymentMethod === 'WALLET';

  return (
    <>
      {!isWallet && selectedMethodButton}

      {isWallet && (
        <>
          {walletAvailability === 'checking' && (
            <Skeleton variant="rectangular" width="100%" height={48} />
          )}
          {walletAvailability !== 'unavailable' && (
            <ExpressCheckoutElement
              options={{
                paymentMethods: {
                  applePay:
                    availableWalletPaymentMethod === PAYMENT_METHOD.APPLE_PAY
                      ? 'auto'
                      : 'never',
                  googlePay:
                    availableWalletPaymentMethod === PAYMENT_METHOD.GOOGLE_PAY
                      ? 'auto'
                      : 'never',
                  link: 'never',
                  paypal: 'never',
                  amazonPay: 'never',
                  klarna: 'never',
                },
                buttonType: { applePay: 'rent', googlePay: 'pay' },
                buttonHeight: 48,
              }}
              onReady={handleReady}
              onClick={handleWalletClick}
              onConfirm={handleWalletConfirm}
              onCancel={handleWalletCancel}
            />
          )}
          {/* Wallet selected but unavailable on this browser: fall back to the
              standard button, which surfaces the "not supported" message. */}
          {walletAvailability === 'unavailable' && selectedMethodButton}
        </>
      )}

      <PreAuthFailedDialog
        open={isPaymentErrorDialogOpen}
        onClose={handleClosePaymentErrorDialog}
      />
    </>
  );
};

/**
 * Legacy wallet flow: deprecated `stripe.paymentRequest()` +
 * `PaymentRequestButtonElement`. Kept verbatim behind the
 * `MULTI_CARDS_PER_USER_BY_WEB_PLATFORM` flag and removed once the new flow is
 * fully rolled out.
 */
const LegacyRentChargerButton = ({ disabled = false }: Props) => {
  const { selectedPaymentMethod, selectedPricing } = usePaymentStepContext();
  const { availableWalletPaymentMethod } = useUserAgent();
  const [
    isCheckingPaymentRequestAvailability,
    setIsCheckingPaymentRequestAvailability,
  ] = useState(true);

  const stripe = useStripe();
  const [searchParams] = useSearchParams();
  const stationSerialNumber = searchParams.get('box');
  const {
    handleRentCharger,
    isRentingCharger,
    handleSetIsRentingCharger,
    isPaymentErrorDialogOpen,
    handleClosePaymentErrorDialog,
  } = useRentCharger();

  const { stationPricing } = useStationPricingContext();

  const [paymentRequest, setPaymentRequest] = useState<PaymentRequest | null>(
    null,
  );

  const { recordBtnClicked } = useEventTrackingContext();

  const handleRentChargerForSelectedMethod = useCallback(
    (event: SyntheticEvent<HTMLButtonElement>) => {
      if (!selectedPaymentMethod) {
        return;
      }

      if (selectedPaymentMethod === 'WALLET') {
        toast.error('Wallet not supported for this browser');
        return;
      }

      // Campus card: charge via the campus-card gateway. The id is the linked
      // campus card payment id; no Stripe `method` applies.
      if (isSelectedCampusCard(selectedPaymentMethod)) {
        handleRentCharger(
          {
            id: selectedPaymentMethod.card.id,
            gateway: PAYMENT_GATEWAY.CAMPUS_CARD,
          },
          event,
          undefined,
          selectedPricing?.id,
        );
        recordBtnClicked(TRACKING_BUTTON.EJECT_BATTERY, {
          paymentMethod: PAYMENT_GATEWAY.CAMPUS_CARD,
        });
        return;
      }

      handleRentCharger(
        {
          id: selectedPaymentMethod.paymentMethodId,
          method: PAYMENT_METHOD.CREDIT_CARD,
        },
        event,
        undefined,
        selectedPricing?.id,
      );
      recordBtnClicked(TRACKING_BUTTON.EJECT_BATTERY, {
        paymentMethod: PAYMENT_METHOD.CREDIT_CARD,
      });
    },
    [
      handleRentCharger,
      recordBtnClicked,
      selectedPaymentMethod,
      selectedPricing,
    ],
  );

  useEffect(() => {
    if (stripe && !paymentRequest) {
      const pr = stripe.paymentRequest({
        country: 'US',
        currency: stationPricing?.currency.toLowerCase() || 'usd',
        total: {
          label: 'Prepare Payment (no charge)',
          amount: 0,
        },
        requestPayerName: true,
        requestPayerEmail: true,
      });

      // Check the availability of the Payment Request API.
      pr.canMakePayment()
        .then((result) => {
          if (
            result?.applePay &&
            availableWalletPaymentMethod === PAYMENT_METHOD.APPLE_PAY
          ) {
            setPaymentRequest(pr);
          }
          if (
            result?.googlePay &&
            availableWalletPaymentMethod === PAYMENT_METHOD.GOOGLE_PAY
          ) {
            setPaymentRequest(pr);
          }
        })
        .finally(() => {
          setIsCheckingPaymentRequestAvailability(false);
        });
    }
  }, [
    stripe,
    stationSerialNumber,
    availableWalletPaymentMethod,
    paymentRequest,
    stationPricing?.currency,
  ]);

  const handlePaymentMethod = useCallback(
    async (event: PaymentRequestPaymentMethodEvent) => {
      try {
        await handleRentCharger(
          {
            id: event.paymentMethod.id,
            method: availableWalletPaymentMethod,
          },
          undefined,
          undefined,
          selectedPricing?.id,
        );

        recordBtnClicked(TRACKING_BUTTON.EJECT_BATTERY, {
          paymentMethod: availableWalletPaymentMethod,
        });

        event.complete('success');
      } catch (err) {
        event.complete('fail');
      }
    },
    [
      availableWalletPaymentMethod,
      handleRentCharger,
      recordBtnClicked,
      selectedPricing,
    ],
  );

  useEffect(() => {
    if (!paymentRequest) return;

    paymentRequest.on('paymentmethod', handlePaymentMethod);

    return () => {
      paymentRequest.off('paymentmethod', handlePaymentMethod);
    };
  }, [handlePaymentMethod, paymentRequest]);

  const handleCancel = useCallback(() => {
    handleSetIsRentingCharger(false);
  }, [handleSetIsRentingCharger]);

  useEffect(() => {
    if (!paymentRequest) return;

    paymentRequest.on('cancel', handleCancel);

    return () => {
      paymentRequest.off('cancel', handleCancel);
    };
  }, [handleCancel, paymentRequest]);

  // Show the Payment Request Button only if it's available and the user has selected the wallet payment method
  // and is not renting the charger
  const showPaymentRequestButton =
    !isCheckingPaymentRequestAvailability &&
    paymentRequest &&
    selectedPaymentMethod === 'WALLET' &&
    !isRentingCharger &&
    !disabled;

  const showLoading = isCheckingPaymentRequestAvailability;

  return (
    <>
      {showLoading ? (
        <Skeleton variant="rectangular" width="100%" height={48} />
      ) : (
        <>
          {showPaymentRequestButton ? (
            <PaymentRequestButtonElement
              options={{ paymentRequest }}
              onClick={() => handleSetIsRentingCharger(true)}
            />
          ) : (
            <LoadingButton
              variant="contained"
              color="primary"
              onClick={handleRentChargerForSelectedMethod}
              isLoading={isRentingCharger}
              disabled={isRentingCharger || disabled}
            >
              <LocalizationTypography
                variant="Poppins/Subheading-md"
                textId="app.rentalPage.newStepThree.text.rentCharger"
                defaultText="Rent Charger"
              />
            </LoadingButton>
          )}
        </>
      )}

      <PreAuthFailedDialog
        open={isPaymentErrorDialogOpen}
        onClose={handleClosePaymentErrorDialog}
      />
    </>
  );
};

export const RentChargerButton = ({ disabled = false }: Props) => {
  const isMultiCardsPerUserEnabled = checkEnabledFeatureFlag(
    FEATURE_FLAGS.MULTI_CARDS_PER_USER_BY_WEB_PLATFORM,
  );

  return isMultiCardsPerUserEnabled ? (
    <ExpressRentChargerButton disabled={disabled} />
  ) : (
    <LegacyRentChargerButton disabled={disabled} />
  );
};
